// Right-hand drawer to view + edit a single lead, with an audit trail tab.

import { useEffect, useState } from 'react';
import { STAGES, PRODUCTS, SOURCES } from '../lib/stages';
import { updateLead, deleteLead } from '../lib/leads';
import { getActivity } from '../lib/activity';
import { useAuth } from '../hooks/useAuth';

export default function LeadDrawer({ lead, onClose }) {
  const { profile } = useAuth();
  const [tab, setTab] = useState('details');
  const [form, setForm] = useState(() => ({ ...lead }));
  const [activity, setActivity] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm({ ...lead }); }, [lead?.id]);

  useEffect(() => {
    if (tab === 'activity' && lead?.id) {
      getActivity(lead.id).then(setActivity).catch(console.error);
    }
  }, [tab, lead?.id]);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function save() {
    setSaving(true);
    try {
      // Strip server-managed fields before sending
      const { id, createdAt, updatedAt, lastActivityAt, createdBy, updatedBy, ...patch } = form;
      patch.estimatedMonthlyValueKES = Number(patch.estimatedMonthlyValueKES || 0);
      await updateLead(lead.id, patch);
      onClose?.();
    } finally { setSaving(false); }
  }

  async function remove() {
    if (!confirm(`Delete lead "${lead.companyName}"? This cannot be undone.`)) return;
    await deleteLead(lead.id);
    onClose?.();
  }

  if (!lead) return null;
  const canEdit = profile?.role === 'admin' || lead.ownerId === profile?.uid;

  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-lg bg-white shadow-xl h-full flex flex-col">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <div className="text-lg font-semibold">{lead.companyName || '(no name)'}</div>
            <div className="text-xs text-slate-500">
              {lead.contactName}
              {lead.contactTitle ? `, ${lead.contactTitle}` : ''}
              {lead.industry ? ` · ${lead.industry}` : ''}
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost">✕</button>
        </div>
        <div className="px-5 pt-3 border-b text-sm flex gap-3">
          <button className={`pb-2 ${tab==='details'?'border-b-2 border-brand-600 text-brand-700':'text-slate-500'}`} onClick={() => setTab('details')}>Details</button>
          <button className={`pb-2 ${tab==='activity'?'border-b-2 border-brand-600 text-brand-700':'text-slate-500'}`} onClick={() => setTab('activity')}>Activity</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {tab === 'details' && (
            <>
              <Field label="Company">         <input className="input" value={form.companyName||''} onChange={e=>set('companyName', e.target.value)} disabled={!canEdit} /></Field>
              <Field label="Industry">        <input className="input" value={form.industry||''} onChange={e=>set('industry', e.target.value)} disabled={!canEdit} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Contact name">  <input className="input" value={form.contactName||''} onChange={e=>set('contactName', e.target.value)} disabled={!canEdit} /></Field>
                <Field label="Contact title"> <input className="input" value={form.contactTitle||''} onChange={e=>set('contactTitle', e.target.value)} placeholder="e.g. CFO, IT Manager" disabled={!canEdit} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Phone"><input className="input" value={form.phone||''} onChange={e=>set('phone', e.target.value)} disabled={!canEdit} /></Field>
                <Field label="Email"><input className="input" value={form.email||''} onChange={e=>set('email', e.target.value)} disabled={!canEdit} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Stage">
                  <select className="input" value={form.stage} onChange={e=>set('stage', e.target.value)} disabled={!canEdit}>
                    {STAGES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Product Interest">
                  <select className="input" value={form.productInterest} onChange={e=>set('productInterest', e.target.value)} disabled={!canEdit}>
                    {PRODUCTS.map(p => <option key={p}>{p}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Lead source">
                  <select className="input" value={form.leadSource} onChange={e=>set('leadSource', e.target.value)} disabled={!canEdit}>
                    {SOURCES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Estimated KES / month">
                  <input className="input" type="number" min="0" value={form.estimatedMonthlyValueKES||0} onChange={e=>set('estimatedMonthlyValueKES', e.target.value)} disabled={!canEdit} />
                </Field>
              </div>
              <Field label="Next action"><input className="input" value={form.nextAction||''} onChange={e=>set('nextAction', e.target.value)} disabled={!canEdit} /></Field>
              <Field label="Next action date">
                <input className="input" type="date"
                       value={toDateInput(form.nextActionDate)}
                       onChange={e=>set('nextActionDate', e.target.value ? new Date(e.target.value) : null)}
                       disabled={!canEdit} />
              </Field>
              <Field label="Notes">
                <textarea className="input min-h-[100px]" value={form.notes||''} onChange={e=>set('notes', e.target.value)} disabled={!canEdit}/>
              </Field>
            </>
          )}

          {tab === 'activity' && (
            <ul className="space-y-2 text-sm">
              {activity.length === 0 && <li className="text-slate-500">No activity yet.</li>}
              {activity.map(a => (
                <li key={a.id} className="card p-3">
                  <div className="font-medium">{a.action.replace('_', ' ')}</div>
                  <div className="text-xs text-slate-500">
                    {a.userName || 'Unknown'} · {a.at?.toDate ? a.at.toDate().toLocaleString() : ''}
                  </div>
                  {a.after && Object.keys(a.after).length > 0 && (
                    <pre className="text-[11px] mt-1 text-slate-700 whitespace-pre-wrap">{JSON.stringify(a.after, null, 2)}</pre>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {canEdit && tab === 'details' && (
          <div className="px-5 py-3 border-t flex items-center justify-between">
            <button onClick={remove} className="btn-danger">Delete</button>
            <button onClick={save} disabled={saving} className="btn-primary">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}

function toDateInput(v) {
  if (!v) return '';
  const d = v.toDate ? v.toDate() : new Date(v);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}
